import { useCallback, useEffect, useMemo } from 'react'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { useMutation } from '@apollo/client/react'
import { Trans, useLingui } from '@lingui/react/macro'

import { useCloseModal } from '@/Router'
import Loading from '@/components/ui/Loading'
import { StepTransitions } from '@/components/ui/StepTransitions'
import Button from '@/components/ui/Button'
import { useFeatures } from '../hooks/useFeatures'
import { ConnectorSteps } from './ConnectorSteps'
import { useFeatureSteps } from '../hooks/useFeatureSteps'
import { DynamicStepComponent } from './DynamicStepComponent'
import { ConnectedView } from './ConnectedView'
import { useConnector } from '../hooks/useConnector'
import { useFeature } from '../hooks/useFeature'
import { showToast, ToastType } from '@/features/toast'

function FeatureForms({
  feature,
  refetch
}: {
  feature: GraphQL.IntegrationFeatureFragmentFragment
  refetch: () => void
}) {
  const steps = useFeatureSteps(
    feature.type,
    feature.provider,
    feature.connectionData ? JSON.parse(feature.connectionData) : undefined
  )

  const [update] = useMutation(AdminAPI.integration.FINISH_FEATURE, {
    refetchQueries: [
      AdminAPI.integration.GET_ALL_FEATURES,
      AdminAPI.integration.GET_FEATURE
    ]
  })

  const onSubmit = useCallback(
    (data: object) => {
      return update({
        variables: {
          id: feature.id,
          data: JSON.stringify(data)
        }
      }).then(({ error }) => {
        if (error) return Promise.reject(error)
        return Promise.resolve()
      })
    },
    [feature, update]
  )

  // If we have no steps this shouldn't show up anymore
  if (!steps) {
    return (
      <div className="flex flex-row items-center justify-center text-red-500 h-20 w-full">
        <span>
          <Trans>
            There was a problem setting up the integration, please write to our
            support.
          </Trans>
        </span>
      </div>
    )
  }

  return (
    <StepTransitions
      steps={steps.map(step => ({
        Component: DynamicStepComponent({ step, onSubmit, onReload: refetch }),
        label: step.title,
        icon: step.icon,
        value: step.id
      }))}
      currentStep={feature.step}
    />
  )
}

export type FeatureStepsProps = {
  provider: GraphQL.Providers
  type: GraphQL.IntegrationTypes
}

export function FeatureSteps({ provider, type }: FeatureStepsProps) {
  const { t } = useLingui()
  const features = useFeatures()
  const closeModal = useCloseModal()

  const { connector: currentConnector } = useConnector(provider)

  const featureId = useMemo(() => {
    return (
      features?.find(f => f.provider === provider && f.type === type)?.id ??
      null
    )
  }, [features, provider, type])

  const { feature: currentFeature, refetch } = useFeature(featureId)

  const [create] = useMutation(AdminAPI.integration.CREATE_FEATURE, {
    refetchQueries: [AdminAPI.integration.GET_ALL_FEATURES]
  })

  const [remove, { loading }] = useMutation(
    AdminAPI.integration.DELETE_FEATURE,
    {
      refetchQueries: [AdminAPI.integration.GET_ALL_FEATURES],
      update(cache) {
        if (!currentFeature) return

        cache.evict({
          id: cache.identify({
            __typename: 'IntegrationFeature',
            id: currentFeature?.id
          })
        })

        cache.evict({
          id: cache.identify({
            __typename: 'IntegrationConnector',
            provider: currentFeature?.provider
          })
        })

        cache.gc()
      },
      onCompleted: closeModal
    }
  )

  useEffect(() => {
    if (currentFeature) return
    if (!currentConnector) return
    if (!currentConnector.isConnected) return

    create({
      variables: {
        provider,
        type
      }
    })
      .then(() => null)
      .catch(() => {
        showToast({
          id: 'unknown-error',
          type: ToastType.ERROR,
          message: t`There was an unexpected issue creating the feature. Please write our support if this keeps happening.`
        })

        closeModal()
      })
  }, [currentFeature, currentConnector, provider, type, create, t, closeModal])

  if (!currentConnector || !currentConnector.isConnected) {
    return <ConnectorSteps provider={provider} />
  }

  if (!currentFeature) {
    return (
      <div className="flex flex-row justify-center items-center h-20 w-full">
        <Loading />
      </div>
    )
  }

  if (currentFeature.isSetupDone) {
    return <ConnectedView feature={currentFeature} />
  }

  return (
    <div className="flex flex-col gap-1">
      <FeatureForms feature={currentFeature} refetch={refetch} />
      <div className="flex flex-row justify-start">
        <Button
          onClick={() => remove({ variables: { id: currentFeature.id } })}
          loading={loading}
        >
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </div>
  )
}
